import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { startRegistration } from "@simplewebauthn/browser";
import { useAuth } from "../../hooks/useAuth";
import { authApi } from "../../api/auth";
import { formatApiError } from "../../lib/errors";
import { metaEventId, trackEvent, trackEventOnce, trackMetaEvent } from "../../lib/analytics";
import { PASSWORD_HINT_KEY, PASSWORD_LIMITS, validatePassword, confirmError } from "../../lib/password";
import { leadsApi } from "../../api/leads";
import ResendVerification from "./ResendVerification";
import PasswordField from "./PasswordField";
import BrandHeader from "../BrandHeader";

// Meta deduplicates on (event_name, event_id), so the pixel copy of
// CompleteRegistration and the server copy have to name the same string. That
// string is derived from the email in this form (see metaEventId in
// lib/analytics.js), which the server also knows for the account it just
// created — so it works for every signup, including one that arrived without
// a ?rt= resume link.

export default function SignupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const { signup } = useAuth();
  const { t } = useTranslation("auth");

  // Arriving from the mobile capture email: /signup?rt=<resume_token>. Resolve
  // the token to an address and prefill it, so the desktop form is one field
  // shorter. A stale or forged token resolves to null and this is a no-op, so
  // the visitor just sees a normal empty form.
  useEffect(() => {
    const rt = new URLSearchParams(window.location.search).get("rt");
    if (!rt) return;
    // trackEventOnce guards on localStorage, so refreshing the prefilled form
    // does not count the same return twice. Pass the bare key: the helper
    // prepends `tm_evt_` itself (see lib/analytics.js), so prefixing here
    // would produce `tm_evt_tm_evt_...`.
    trackEventOnce(`resume_${rt.slice(0, 12)}`, "lead_resume_click", {
      via: "email",
    });
    leadsApi
      .resolveResume(rt)
      .then((resp) => {
        const resolved = resp.data && resp.data.email;
        if (!resolved) return;
        // Prefill is a convenience, never a correction: if the visitor has
        // already started typing, their input wins.
        setEmail((current) => (current ? current : resolved));
      })
      .catch(() => {});
  }, []);

  // Marketing opt-in is an explicit, unchecked-by-default checkbox at signup.
  // Everyone can also flip it later from /dashboard/settings.

  // A signup is a lead by any definition — an address, freely given, with an
  // account attached. It is reported as one so the campaign optimising on
  // `Lead` can learn from it, alongside `CompleteRegistration` which describes
  // the deeper step. Stacking funnel events on one action is what Meta expects.
  //
  // Both this and the /ig email capture derive the id from sha256 of the
  // normalised address, so the same person doing both produces one identifier
  // and Meta collapses them — within its roughly 48-hour dedup window. Someone
  // who leaves an email on Monday and signs up on Thursday is counted twice.
  // Both are real actions by a real person, so that inflates the count without
  // poisoning what the algorithm learns.
  //
  // Pixel only, which covers less than it looks like. The server copy needs an
  // `fbc` and we only hold one for someone who left an email first, so a
  // visitor who clicked the ad and signed up straight away is reported from
  // this browser alone — fine for the same-device case the cookie describes,
  // but silent for anyone the consent gate or an ad blocker keeps the pixel
  // from loading. That is an undercount, not a wrong count. Closing it means a
  // CAPI copy built from the `_fbc` cookie on the signup request itself.
  const reportSignup = async (method) => {
    const eventId = await metaEventId("signup", email);
    trackEvent("sign_up", { method });
    trackMetaEvent("Lead", await metaEventId("lead", email), { content_name: "signup" });
    trackMetaEvent("CompleteRegistration", eventId, { content_name: method });
  };

  // Live, so the mismatch shows while they are still looking at the field
  // rather than after a rejected submit.
  const mismatch = confirmError(password, confirm) !== null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Checked before strength: an obvious mismatch must not be buried under a
    // lecture about symbols.
    // No top-level banner for this one: the inline message under the second
    // field and its red border already say it, and a banner survives the fix —
    // it would sit there claiming a mismatch above two fields that now agree.
    if (confirmError(password, confirm) || password !== confirm) {
      return;
    }
    const pwdError = validatePassword(password);
    if (pwdError) {
      setError(t(pwdError, PASSWORD_LIMITS));
      return;
    }
    setLoading(true);
    try {
      await signup(email, password, marketingConsent);
      await reportSignup("password");
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err, t("signup.errorFallback")));
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeySignup = async () => {
    setError("");
    if (!email.trim()) {
      setError(t("signup.passkeyNeedEmail"));
      return;
    }
    setPasskeyLoading(true);
    try {
      const startResp = await authApi.passkeySignupStart({ email, marketing_consent: marketingConsent });
      const { challenge_id, options } = startResp.data;
      const credential = await startRegistration(options);
      await authApi.passkeySignupComplete({ challenge_id, credential });
      await reportSignup("passkey");
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err, err.message || t("signup.passkeyErrorFallback")));
    } finally {
      setPasskeyLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <BrandHeader />
        <div className="flex items-center justify-center min-h-[calc(100vh-6rem)] px-6">
          <div className="w-full max-w-md feature-card p-8 rounded-lg text-center">
            <h1 className="font-clash text-2xl font-bold text-white mb-4">{t("signup.checkEmailTitle")}</h1>
            <p className="text-[#A1A1AA] mb-6">
              {t("signup.checkEmailPrefix")} <span className="text-white">{email}</span>{t("signup.checkEmailSuffix")}
            </p>
            <p className="text-xs text-[#A1A1AA] mb-2">{t("signup.didntGetIt")}</p>
            <ResendVerification email={email} className="mb-6 flex flex-col items-center" />
            <Link to="/login" className="text-[#FF5500] hover:text-[#FF7733] text-sm">{t("signup.backToLogin")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <div className="flex items-center justify-center min-h-[calc(100vh-6rem)] px-6">
        <div className="w-full max-w-md">
        <div className="feature-card p-8 rounded-lg">
          <h1 className="font-clash text-2xl font-bold text-white mb-6 text-center">{t("signup.title")}</h1>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded mb-4 text-sm">{error}</div>
          )}
          {/* Above both methods and outside the form, because it belongs to
              both: a passkey account needs an address just as much as a
              password one does. Inside the form it read as the first step of
              the password path, and someone heading for the passkey button
              could reach it without having typed anything. */}
          <div className="mb-4">
            <label htmlFor="signup-email" className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("signup.emailLabel")}</label>
            {/* `form` ties it back to the password form it sits outside of, so
                native validation still blocks an empty submit and Enter still
                submits — both of which are lost by moving an input out of its
                form. */}
            <input id="signup-email" name="email" form="signup-password-form" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none transition-colors" required />
          </div>
          <form id="signup-password-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("signup.passwordLabel")}</label>
              <PasswordField value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password" required minLength={PASSWORD_LIMITS.min} />
              <p className="text-xs text-[#A1A1AA] mt-1">{t(PASSWORD_HINT_KEY, PASSWORD_LIMITS)}</p>
            </div>
            {/* A typo here is the one that never surfaces: the account is created
                with a value its owner never knew, and every later login insists
                the password is wrong. */}
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("password.confirmLabel")}</label>
              <PasswordField value={confirm} onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password" invalid={!!mismatch} required minLength={PASSWORD_LIMITS.min} />
              {mismatch && <p className="text-xs text-red-400 mt-1">{t("password.mismatch")}</p>}
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-white/10 bg-[#1a1a1e] text-[#FF5500] accent-[#FF5500] focus:outline-none"
              />
              <span className="text-sm text-[#A1A1AA]">
                {t("signup.marketingConsent")}
              </span>
            </label>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3 rounded text-sm font-semibold disabled:opacity-50">
              {loading ? t("signup.submitting") : t("signup.submit")}
            </button>
            <p className="text-[10px] leading-relaxed text-[#A1A1AA]/70 text-center">
              {t("signup.termsPrefix")}{" "}
              <Link to="/privacy" className="underline hover:text-[#FF5500]">{t("signup.privacyPolicy")}</Link>{t("signup.termsSuffix")}
            </p>
          </form>
          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-[#A1A1AA]">{t("signup.or")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <button
            onClick={handlePasskeySignup}
            disabled={passkeyLoading}
            className="w-full py-3 rounded border border-white/10 text-white text-sm font-medium hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            {passkeyLoading ? t("signup.passkeyWaiting") : t("signup.passkeyButton")}
          </button>
          <p className="text-xs text-[#A1A1AA] text-center mt-3">
            {t("signup.passkeyHint")}
          </p>
          <p className="text-center text-sm text-[#A1A1AA] mt-6">
            {t("signup.haveAccount")}{" "}
            <Link to="/login" className="text-[#FF5500] hover:text-[#FF7733]">{t("signup.logIn")}</Link>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
